import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, CheckCircle2, ArrowRight, Building2, User } from "lucide-react";
import { SignUpSchema } from '../lib/schemas';
import axios from 'axios';
import { useDropdown, useCities, PhoneField } from '../contexts/DropdownContext';
import { useAuth } from '../contexts/AuthContext';
import { useDataMode } from '../contexts/DataModeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPLIER_BENEFITS = [
  'Showcase products to 500+ global exhibitors',
  'Receive targeted product inquiries',
  'Build a verified supplier catalogue',
  'Transparent pricing & secure ordering',
  'Manage orders from a single dashboard',
  'Access to events across 46+ countries',
];

const SupplierSignup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isMockMode, toggleMode } = useDataMode();
  const { countries } = useDropdown();
  const [orgCountry, setOrgCountry] = useState('');
  const [phoneField, setPhoneField] = useState({ code: '+90', number: '' });
  const { cities } = useCities(orgCountry);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const [formData, setFormData] = useState({
    role: 'SUPPLIER',
    org: { display_name: '', country: '', city: '', website: '' },
    user: { name: '', email: '', phone_e164: '', password: '', confirmPassword: '' },
    accept_terms: false
  });

  const handleInputChange = (field, value, parentPath = []) => {
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (const path of parentPath) current = current[path];
      current[field] = value;
      return newData;
    });
    if (serverError) setServerError('');
  };

  const fillTestData = () => {
    setFormData({
      role: 'SUPPLIER',
      org: { display_name: 'Eurostand Materials B.V.', country: 'NL', city: 'Amsterdam', website: 'https://eurostand-materials.nl' },
      user: { name: 'Jan van den Berg', email: 'j.vandenberg@eurostand-materials.nl', phone_e164: '', password: '123456Ab&', confirmPassword: '123456Ab&' },
      accept_terms: true,
    });
    setOrgCountry('NL');
    setBaseCurrency('EUR');
    setPhoneField({ code: '+31', number: '612345678' });
    setErrors({});
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setServerError('');
    try {
      if (!phoneField.number || phoneField.number.length < 6) {
        setErrors(prev => ({ ...prev, 'user.phone_e164': 'Please enter a valid phone number (min 6 digits)' }));
        setLoading(false);
        return;
      }
      const formWithPhone = { ...formData, user: { ...formData.user, phone_e164: phoneField.code + phoneField.number } };
      const validatedData = SignUpSchema.parse(formWithPhone);
      const { confirmPassword, ...userDataToSend } = validatedData.user;
      await axios.post(`${BACKEND_URL}/api/auth/signup`, { ...validatedData, user: userDataToSend, language_preference: localStorage.getItem('expostand_lang') || 'en', base_currency: baseCurrency });
      // Auto-login after successful signup
      const result = await login(formData.user.email, formData.user.password);
      if (result.success) {
        if (isMockMode) toggleMode();
        // New supplier → onboarding wizard (unless already completed)
        const onboardingDone = localStorage.getItem('supplier_onboarding_done');
        navigate(onboardingDone ? '/app/supplier/dashboard' : '/app/supplier/onboarding');
      } else {
        navigate('/supplier/login', { state: { email: formData.user.email, message: 'Account created! Please log in.' } });
      }
    } catch (error) {
      if (error.name === 'ZodError') {
        const fieldErrors = {};
        (error.issues || error.errors || []).forEach(err => { fieldErrors[err.path.join('.')] = err.message; });
        setErrors(fieldErrors);
      } else if (error.response?.data?.detail) {
        const d = error.response.data.detail;
        setServerError(typeof d === 'string' ? d : d.message || 'An error occurred during registration.');
      } else {
        setServerError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (fieldPath) => errors[fieldPath];

  const inputClass = (err) =>
    `w-full bg-white border text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors placeholder-gray-400 ${err ? 'border-red-400' : 'border-gray-200 focus:border-purple-500'}`;

  return (
    <div className="min-h-screen flex bg-gray-950">
      {/* Left Panel — Purple */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-purple-900 via-purple-800 to-violet-900 flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 40%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 60%, #fff 1px, transparent 1px)', backgroundSize:'40px 40px'}} />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2 text-white font-black text-xl tracking-tight">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-purple-700 font-black text-sm">ES</span>
            </div>
            <span>EXPO STAND</span>
          </Link>
        </div>
        <div className="relative space-y-8">
          <div>
            <span className="text-purple-300 text-xs font-bold uppercase tracking-widest">Supplier Portalı</span>
            <h1 className="text-4xl font-black text-white mt-2 leading-tight">
              Ürünlerini<br /><span className="text-purple-300">Dünyaya Ulaştır</span>
            </h1>
            <p className="text-purple-200 mt-4 text-sm leading-relaxed">
              Kaliteli ürün arayan global fuar katılımcılarıyla buluş. 46+ ülkede erişim sağla.
            </p>
          </div>
          <div className="space-y-3">
            {SUPPLIER_BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-purple-700 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-purple-200" />
                </div>
                <span className="text-purple-100 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <p className="text-purple-300 text-xs">Zaten hesabın var mı?{' '}
            <button type="button" onClick={() => navigate('/supplier/login')} className="text-white font-semibold hover:underline">Giriş yap →</button>
          </p>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="w-full lg:w-1/2 flex items-start justify-center p-6 sm:p-10 overflow-y-auto bg-white">
        <div className="w-full max-w-md pt-8">
          <div className="mb-7">
            <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-black text-gray-900">Supplier Hesabı Oluştur</h1>
                <p className="text-gray-500 text-sm mt-1">Ücretsiz kaydol, ürün sat</p>
              </div>
              <button type="button" onClick={fillTestData}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-purple-500/50 text-purple-400 text-xs font-semibold hover:bg-purple-900/30 transition-colors"
                data-testid="fill-test-data-btn">
                ⚡ Fill Test Data
              </button>
            </div>
          </div>

          {serverError && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {serverError}
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Company Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-purple-700 flex items-center justify-center">
                  <Building2 className="w-3 h-3 text-purple-200" />
                </div>
                <span className="text-purple-600 text-xs font-bold uppercase tracking-widest">Şirket Bilgileri</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Şirket Adı *</label>
                  <input
                    data-testid="input-company"
                    type="text"
                    placeholder="E.g. Global Supplies Ltd."
                    value={formData.org.display_name}
                    onChange={(e) => handleInputChange('display_name', e.target.value, ['org'])}
                    className={inputClass(getFieldError('org.display_name'))}
                  />
                  {getFieldError('org.display_name') && <p className="text-red-500 text-xs mt-1">{getFieldError('org.display_name')}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Ülke *</label>
                  <select
                    data-testid="select-country"
                    value={formData.org.country}
                    onChange={e => { handleInputChange('country', e.target.value, ['org']); setOrgCountry(e.target.value); handleInputChange('city', '', ['org']); }}
                    className={inputClass(getFieldError('org.country'))}
                  >
                    <option value="">Select country</option>
                    {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                  </select>
                  {getFieldError('org.country') && <p className="text-red-500 text-xs mt-1">{getFieldError('org.country')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Şehir</label>
                  {cities.length > 0 ? (
                    <select value={formData.org.city} onChange={e => handleInputChange('city', e.target.value, ['org'])}
                      className={inputClass(getFieldError('org.city'))}>
                      <option value="">Select city</option>
                      {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input type="text" placeholder="Istanbul" value={formData.org.city}
                      onChange={(e) => handleInputChange('city', e.target.value, ['org'])}
                      className={inputClass(getFieldError('org.city'))} />
                  )}
                  {getFieldError('org.city') && <p className="text-red-500 text-xs mt-1">{getFieldError('org.city')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Website</label>
                  <input type="text" placeholder="www.sirketiniz.com" value={formData.org.website}
                    onChange={(e) => handleInputChange('website', e.target.value, ['org'])}
                    className={inputClass(false)} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Base Currency *</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { code: 'USD', symbol: '$' },
                      { code: 'EUR', symbol: '€' },
                      { code: 'GBP', symbol: '£' },
                      { code: 'TRY', symbol: '₺' },
                      { code: 'AED', symbol: 'د.إ' },
                    ].map(c => (
                      <button key={c.code} type="button" data-testid={`currency-${c.code}`}
                        onClick={() => setBaseCurrency(c.code)}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border-2 text-xs font-semibold transition-all ${
                          baseCurrency === c.code ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:border-purple-400'
                        }`}>
                        <span className="text-base leading-none mb-0.5">{c.symbol}</span>
                        <span>{c.code}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Your default currency for catalog pricing. You can also price products in other currencies.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-800" />

            {/* User Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center">
                  <User className="w-3 h-3 text-gray-300" />
                </div>
                <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">Kişisel Bilgiler</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Ad Soyad *</label>
                  <input data-testid="input-name" type="text" placeholder="Your Full Name"
                    value={formData.user.name}
                    onChange={(e) => handleInputChange('name', e.target.value, ['user'])}
                    className={inputClass(getFieldError('user.name'))} />
                  {getFieldError('user.name') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.name')}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">E-posta *</label>
                  <input data-testid="input-email" type="email" placeholder="isim@sirket.com"
                    value={formData.user.email}
                    onChange={(e) => handleInputChange('email', e.target.value, ['user'])}
                    className={inputClass(getFieldError('user.email'))} />
                  {getFieldError('user.email') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.email')}</p>}
                </div>
                <div className="col-span-2">
                  <PhoneField value={phoneField} onChange={setPhoneField} label="Phone" required />
                  {getFieldError('user.phone_e164') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.phone_e164')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Şifre *</label>
                  <div className="relative">
                    <input data-testid="input-password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                      value={formData.user.password}
                      onChange={(e) => handleInputChange('password', e.target.value, ['user'])}
                      className={inputClass(getFieldError('user.password')) + ' pr-10'} />
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {getFieldError('user.password') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.password')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Şifre Tekrar *</label>
                  <div className="relative">
                    <input data-testid="input-confirm-password" type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••"
                      value={formData.user.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value, ['user'])}
                      className={inputClass(getFieldError('user.confirmPassword')) + ' pr-10'} />
                    <button type="button" onClick={() => setShowConfirmPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {getFieldError('user.confirmPassword') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.confirmPassword')}</p>}
                </div>
              </div>
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5 shrink-0">
                <input type="checkbox" data-testid="checkbox-terms"
                  checked={formData.accept_terms}
                  onChange={(e) => handleInputChange('accept_terms', e.target.checked)}
                  className="sr-only" />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  formData.accept_terms ? 'bg-purple-600 border-purple-600' : 'border-gray-300 group-hover:border-purple-400'
                }`}>
                  {formData.accept_terms && (
                    <svg viewBox="0 0 10 8" className="w-3 h-2.5 fill-none stroke-white stroke-2">
                      <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-600 leading-relaxed">
                <a href="#" className="text-purple-400 hover:underline font-medium">Kullanım Şartları</a> ve{' '}
                <a href="#" className="text-purple-400 hover:underline font-medium">Gizlilik Politikası</a>'nı kabul ediyorum. *
              </span>
            </label>
            {getFieldError('accept_terms') && <p className="text-red-500 text-xs">{getFieldError('accept_terms')}</p>}

            {/* Submit */}
            <button type="submit" data-testid="submit-btn" disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all text-sm">
              {loading ? (
                <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Hesap oluşturuluyor...</>
              ) : (
                <>Hesap Oluştur <ArrowRight size={16} /></>
              )}
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-6">
            Hesap oluşturarak şartlarımızı kabul etmiş olursunuz. Verileriniz GDPR kapsamında korunur.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupplierSignup;
